import { useSelector } from 'react-redux';

import { createOrUpdateBorrower } from '@/apiCalls/borrower';
import { fetchServicing } from '@/apiCalls/fetchServicing';
import { getQuickQuote } from '@/apiCalls/matchingEngine';
import { DisplayButtons } from '@/components/DisplayButtons';
import {
    EEmploymentStatus,
    ELoanUsage,
    selectEmploymentStatusByIndex,
    selectLoanUsage,
    selectMortgageRent,
    selectOtherIncome,
    selectOtherIncomeSource,
    selectPaidAmount,
    selectPaidFrequency,
    setAnnualKms,
    setHecsDebt,
} from '@/context/borrower';
import {
    EDisplays,
    EModals,
    selectSkipExpenseScreen,
    setDisplay,
    setModal,
} from '@/context/display';
import { RootState, store } from '@/context/store';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { selectIsLoggedIn } from '@/context/user';
import { calculateHecsAndNetIncome } from '@/utils/getHecsDebt';

interface IIncomeButtonsProps {
    setShowErrors: (value: boolean) => void;
    baseId: string;
}

export function IncomeButtons({
    setShowErrors,
    baseId,
}: IIncomeButtonsProps): JSX.Element {
    // ***** Redux State *****
    const dispatch = useAppDispatch();
    const employment = useSelector((state: RootState) =>
        selectEmploymentStatusByIndex(state, 0)
    );
    const employmentStatus = employment?.status;
    const paidFrequency = useAppSelector(selectPaidFrequency);
    const amountPaid = useAppSelector(selectPaidAmount);
    const otherIncome = useAppSelector(selectOtherIncome);
    const sourceOfOtherIncome = useAppSelector(selectOtherIncomeSource);
    const skipExpenseScreen = useAppSelector(selectSkipExpenseScreen);
    const rent = useAppSelector(selectMortgageRent);
    const isLoggedIn = useAppSelector(selectIsLoggedIn);

    // ***** Event Handlers *****
    function handleBackClick(): void {
        const backScreen = getBackScreen();
        dispatch(setDisplay(backScreen));
    }

    async function handleNextClick(): Promise<void> {
        setShowErrors(true);
        if (!isComplete()) return;
        const state = store.getState();
        const borrower = state.borrowerReducer;
        const amountPaidInBankAccountMonthly =
            state.borrowerReducer.income.paidAmount || 0;
        const income = state.borrowerReducer.income;
        const isHecsDebtDisplayed = (): boolean => {
            const income = state.borrowerReducer.income;
            if (!income) return false;

            let annualIncome = 0;
            // Convert paid amount to annual based on frequency
            if (income.paidAmount) {
                switch (income.paidFrequency) {
                    case 'WEEKLY':
                        annualIncome =
                            (income.paidAmount + (income.otherIncome || 0)) *
                            52;
                        break;
                    case 'FORTNIGHTLY':
                        annualIncome =
                            (income.paidAmount + (income.otherIncome || 0)) *
                            26;
                        break;
                    case 'MONTHLY':
                        annualIncome =
                            (income.paidAmount + (income.otherIncome || 0)) *
                            12;
                        break;
                    default:
                        annualIncome =
                            income.paidAmount + (income.otherIncome || 0);
                }
            }

            return annualIncome < 65000;
        };
        const isNovatedLease =
            state.borrowerReducer.loan.loanUsage === 'NOVATED';
        if (!isNovatedLease) {
            if (borrower.vehicle && borrower.vehicle.annualKms) {
                store.dispatch(setAnnualKms(undefined));
            }
            if (borrower.income && borrower.income.hecsDebt) {
                store.dispatch(setHecsDebt(undefined));
            }
        } else {
            if (
                borrower.income &&
                borrower.income.hecsDebt &&
                !isHecsDebtDisplayed()
            ) {
                store.dispatch(setHecsDebt(undefined));
            } else {
                const actualHecsRepayment = calculateHecsAndNetIncome(
                    amountPaidInBankAccountMonthly,
                    income.otherIncome || 0,
                    income.paidFrequency
                ).hecsRepayment;
                isHecsDebtDisplayed()
                    ? store.dispatch(setHecsDebt(actualHecsRepayment))
                    : store.dispatch(setHecsDebt(undefined));
            }
        }
        createOrUpdateBorrower();
        const nextScreen = await getNextScreen();
        await getQuickQuote();
        {
            const state = store.getState();
            const employmentStatus =
                state.borrowerReducer.employments.employments[0].status;
            if (
                employmentStatus === EEmploymentStatus.CASUAL &&
                loanUsage === ELoanUsage.NOVATED
            ) {
                if (!isLoggedIn) {
                    dispatch(setModal(EModals.REGISTER));
                    return;
                } else {
                    dispatch(setDisplay(EDisplays.EMPLOYMENTINELIGIBLE));
                    return;
                }
            }
            if (
                state.lenderReducer.lenders?.length === 0 &&
                loanUsage === ELoanUsage.NOVATED
            ) {
                if (!isLoggedIn) {
                    dispatch(setModal(EModals.REGISTER));
                } else {
                    if (employmentStatus === EEmploymentStatus.CASUAL) {
                        dispatch(setDisplay(EDisplays.EMPLOYMENTINELIGIBLE));
                        return;
                    }
                    dispatch(setDisplay(EDisplays.INCOMEINELIGIBLE));
                }
                return;
            } else {
                dispatch(setDisplay(nextScreen));
            }
        }
    }

    // ***** Helpers ******
    function isComplete(): boolean {
        const isNovatedLease = loanUsage === ELoanUsage.NOVATED;
        if (!employmentStatus) return false;
        if (
            employmentStatus === EEmploymentStatus.CASUAL &&
            employment.years === undefined &&
            employment.months === undefined
        )
            return false;
        if (paidFrequency === undefined) return false;
        if (amountPaid === undefined) return false;
        if (
            otherIncome !== undefined &&
            otherIncome !== 0 &&
            sourceOfOtherIncome === undefined
        )
            return false;

        if (skipExpenseScreen && rent === undefined) return false;
        return true;
    }
    const loanUsage = useAppSelector(selectLoanUsage);

    async function getNextScreen(): Promise<EDisplays> {
        if (!skipExpenseScreen) return EDisplays.EXPENSE;

        const servicingMet = await fetchServicing();
        if (servicingMet) {
            return EDisplays.SERVICING_SUCCESS;
        }
        return EDisplays.SERVICING_FAILURE;
    }

    function getBackScreen(): EDisplays {
        return EDisplays.BASIC;
    }
    return (
        <DisplayButtons
            idLeftButton={`${baseId}-button-back`}
            idRightButton={`${baseId}-button-next`}
            rightClick={handleNextClick}
            leftClick={handleBackClick}
        />
    );
}
